import React, { useState, useEffect, useRef } from 'react';
import Pusher from 'pusher-js';
import { Card, Accordion, Badge, ListGroup, Button, Form } from 'react-bootstrap';
import CryptoJS from 'crypto-js';

const AutoGenDisplay = () => {
    const [messages, setMessages] = useState([]);
    const [context, setContext] = useState({});
    const [newMessageCounts, setNewMessageCounts] = useState({});
    const [encryptionKey, setEncryptionKey] = useState('');
    const [channelName, setChannelName] = useState('');
    const [isConnected, setIsConnected] = useState(false);
    const [humanMessage, setHumanMessage] = useState('');
    const [isButtonDisabled, setIsButtonDisabled] = useState(false);
    const messagesEndRef = useRef(null);

    // Function to generate a random encryption key
    const generateKey = () => {
        const randomBytes = CryptoJS.lib.WordArray.random(32);
        return CryptoJS.enc.Base64.stringify(randomBytes);
    };

    // Function to generate a random channel name
    const generateChannelName = () => {
        return `channel_${Math.random().toString(36).substring(2)}`;
    };

    // Fetch the CSRF token for API requests
    const getCsrfToken = () => {
        return document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
    };

    // Function to start agents and initialize encryption
    const startAgents = async () => {
        const key = generateKey();
        const randomChannel = generateChannelName();
        setEncryptionKey(key);
        setChannelName(randomChannel);

        try {
            setIsButtonDisabled(true);

            const response = await fetch('/api/start-autogen', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-TOKEN': getCsrfToken(),
                    'X-Requested-With': 'XMLHttpRequest',
                },
                body: JSON.stringify({
                    channelName: randomChannel,
                    encryptionKey: key,
                }),
            });

            if (!response.ok) {
                throw new Error('Failed to start agents');
            }

            const result = await response.json();
            console.log('Agents started:', result);
        } catch (error) {
            console.error('Error starting agents:', error);
            setIsButtonDisabled(false);
        }
    };

    // Connect to Pusher once the channel name is set
    useEffect(() => {
        if (channelName) {
            console.log('Connecting to Pusher with channel:', channelName);
            connectToPusher(encryptionKey);
        }
    }, [channelName]);

    // Function to connect to Pusher and handle messages
    const connectToPusher = (key) => {
        const pusher = new Pusher('c8abb1aa44569363aaf0', {
            cluster: 'us2',
            encrypted: true
        });

        if (!channelName) {
            console.error("Channel name is not set!");
            return;
        }

        const channel = pusher.subscribe(channelName);
        console.log('Subscribed to channel:', channel.name);

        channel.bind('NewMessage', (e) => {
            console.log('Received message (encrypted):', e.message);
            const encryptedMessage = e.message;

            try {
                // Extract IV and encrypted content from the message
                const ivBase64 = encryptedMessage.slice(0, 24);
                const encryptedContent = encryptedMessage.slice(24);

                const parsedKey = CryptoJS.enc.Base64.parse(key);
                const iv = CryptoJS.enc.Base64.parse(ivBase64);

                // Decrypt the message using AES-256-CBC
                const decrypted = CryptoJS.AES.decrypt(
                    { ciphertext: CryptoJS.enc.Base64.parse(encryptedContent) },
                    parsedKey,
                    { iv: iv, mode: CryptoJS.mode.CBC, padding: CryptoJS.pad.Pkcs7 }
                );

                let decryptedText = decrypted.toString(CryptoJS.enc.Utf8);

                // Remove padding manually based on the last character
                const paddingChar = decryptedText.charCodeAt(decryptedText.length - 1);
                if (paddingChar > 0 && paddingChar <= 16) {
                    decryptedText = decryptedText.slice(0, -paddingChar);
                }

                console.log('Decrypted text without padding:', decryptedText);

                // Parse the decrypted message as JSON
                const decodedMessage = JSON.parse(decryptedText);
                console.log('Decrypted and parsed message:', decodedMessage);

                // Handle context updates or message display
                if (decodedMessage.type === 'context_update') {
                    setContext(prevContext => ({
                        ...prevContext,
                        [decodedMessage.data.key]: decodedMessage.data.value
                    }));
                } else {
                    setMessages(prevMessages => [...prevMessages, decodedMessage]);
                    setNewMessageCounts(prev => ({
                        ...prev,
                        [decodedMessage.agent]: (prev[decodedMessage.agent] || 0) + 1
                    }));
                }
            } catch (error) {
                console.error('Error decrypting message:', error);
            }
        });

        setIsConnected(true);

        return () => {
            pusher.unsubscribe(channelName);
            setIsConnected(false);
        };
    };

    // Function to send human input to the API
    const sendHumanMessage = async () => {
        try {
            await fetch('/api/human-message', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-TOKEN': getCsrfToken(),
                    'X-Requested-With': 'XMLHttpRequest',
                },
                body: JSON.stringify({
                    message: humanMessage,
                    channelName,
                    encryptionKey,
                }),
            });

            setHumanMessage('');
        } catch (error) {
            console.error('Error sending human message:', error);
        }
    };

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages]);

    // Function to group messages by agent
    const groupMessagesByAgent = () => {
        return messages.reduce((acc, message) => {
            const agent = message.agent || 'Unknown Agent';
            if (!acc[agent]) {
                acc[agent] = [];
            }
            acc[agent].push(message);
            return acc;
        }, {});
    };

    // Function to render messages for each agent
    const renderAgentCard = (agent, agentMessages) => {
        return (
            <Card key={agent} className="mb-3">
                <Card.Header>
                    {agent}
                    {newMessageCounts[agent] > 0 && (
                        <Badge bg="primary" className="ms-2">
                            {newMessageCounts[agent]} new
                        </Badge>
                    )}
                </Card.Header>
                <Card.Body>
                    {agentMessages.map((message, index) => (
                        <Accordion key={index}>
                            <Accordion.Item eventKey={index.toString()}>
                                <Accordion.Header onClick={() => setNewMessageCounts(prev => ({ ...prev, [agent]: 0 }))}>
                                    {message.type} - {new Date(message.timestamp * 1000).toLocaleString()}
                                </Accordion.Header>
                                <Accordion.Body>
                                    <p><strong>Data:</strong> {JSON.stringify(message.data)}</p>
                                    <p><strong>Context:</strong> {message.context}</p>
                                    <p><strong>Current State:</strong> {message.current_state}</p>
                                    {message.origin && <p><strong>Origin:</strong> {message.origin}</p>}
                                    {message.destination && <p><strong>Destination:</strong> {message.destination}</p>}
                                </Accordion.Body>
                            </Accordion.Item>
                        </Accordion>
                    ))}
                </Card.Body>
            </Card>
        );
    };

    return (
        <div className="container mt-4">
            <h1 className="mb-4">AutoGen Conversation Display</h1>

            <Form className="mb-4">
                <Button onClick={startAgents} disabled={isButtonDisabled} className="w-100">
                    {isConnected ? 'Connected' : 'Start Agents and Connect'}
                </Button>
            </Form>

            <Form className="mb-4">
                <Form.Group className="mb-3">
                    <Form.Label>Human Input</Form.Label>
                    <Form.Control
                        type="text"
                        value={humanMessage}
                        onChange={(e) => setHumanMessage(e.target.value)}
                        placeholder="Type your input here"
                    />
                </Form.Group>
                <Button onClick={sendHumanMessage} disabled={!isConnected} className="w-100">
                    Send Human Input
                </Button>
            </Form>

            <Card className="mb-4">
                <Card.Header>Current Context</Card.Header>
                <Card.Body>
                    <ListGroup>
                        {Object.entries(context).map(([key, value], index) => (
                            <ListGroup.Item key={index}>
                                <strong>{key}:</strong> {value}
                            </ListGroup.Item>
                        ))}
                    </ListGroup>
                </Card.Body>
            </Card>

            {Object.entries(groupMessagesByAgent()).map(([agent, agentMessages]) =>
                renderAgentCard(agent, agentMessages)
            )}
            <div ref={messagesEndRef} />
        </div>
    );
};

export default AutoGenDisplay;
